import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL, IS_AUTH_REQUIRED } from "../../config";
import storage from "../../utils/storage";

type LoginError = string | null;

const Login = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<LoginError>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!IS_AUTH_REQUIRED) {
      return;
    }
    if (storage.getAuthStatus()) {
      navigate("/user-list", { replace: true });
      return;
    }
    const baseUrl = API_URL || "";
    const verifySession = async () => {
      try {
        const response = await fetch(`${baseUrl}/auth/status/`, {
          method: "GET",
          credentials: "include",
        });
        if (!response.ok) {
          storage.clearAuthStatus();
          return;
        }
        const payload = await response.json();
        const isAuthenticated = Boolean(payload?.data?.is_authenticated);
        if (isAuthenticated) {
          storage.setAuthStatus(true);
          navigate("/user-list", { replace: true });
        } else {
          storage.clearAuthStatus();
        }
      } catch (err) {
        storage.clearAuthStatus();
      }
    };

    verifySession();
  }, [navigate]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    const baseUrl = API_URL || "";

    try {
      const response = await fetch(`${baseUrl}/auth/login/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const message =
          payload?.error ||
          payload?.message ||
          "Unable to sign in with the provided credentials.";
        throw new Error(message);
      }

      storage.setAuthStatus(true);
      setUsername("");
      setPassword("");
      navigate("/user-list", { replace: true });
    } catch (err) {
      storage.clearAuthStatus();
      setError(err instanceof Error ? err.message : "Login failed.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <h1 className="login-title">Sign in</h1>
        {error ? <p className="login-error">{error}</p> : null}
        <form className="login-form" onSubmit={handleSubmit}>
          <label className="login-label" htmlFor="username">
            Username
          </label>
          <input
            id="username"
            name="username"
            type="text"
            autoComplete="username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            className="login-input"
            required
            disabled={isSubmitting}
          />

          <label className="login-label" htmlFor="password">
            Password
          </label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="login-input"
            required
            disabled={isSubmitting}
          />

          <button
            type="submit"
            className="login-submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;

